import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DocsSidebar, { docCategories } from '../components/docs/DocsSidebar';
import MarkdownRenderer from '../components/docs/MarkdownRenderer';
import { useAuth } from '../contexts/AuthContext';
import {
  BookOpenIcon,
  ArrowLeftIcon,
  ChevronRightIcon
} from '@heroicons/react/24/outline';

/**
 * Technical Documentation Page
 *
 * Displays developer documentation for Videxa employees only.
 * Protected by TechnicalDocsProtectedRoute which checks @videxa.co domain.
 *
 * Features:
 * - Sidebar navigation for doc categories
 * - Markdown rendering with syntax highlighting
 * - Responsive layout (sidebar collapses on mobile)
 * - Dark mode support
 */

// Import documentation content
// These will be markdown strings - for now using placeholders
// In production, these would be imported from src/docs/developer/
const docContent = {
  index: `# Videxa Developer Documentation

Welcome to the Videxa technical documentation. This documentation is intended for Videxa developers and is not publicly accessible.

## Quick Links

- **[Architecture](/technical-docs/architecture)** - System architecture and design patterns
- **[Authentication](/technical-docs/authentication)** - SSO, JWT, and auth flows
- **[AgentNexus Frontend](/technical-docs/agentnexus-frontend)** - React frontend development
- **[AgentNexus Backend](/technical-docs/agentnexus-backend)** - FastAPI backend APIs
- **[NexusChat](/technical-docs/nexuschat)** - Chat application documentation
- **[Database](/technical-docs/database)** - Snowflake schema and queries
- **[Deployment](/technical-docs/deployment)** - Docker and Azure deployment
- **[Security](/technical-docs/security)** - Security audit and compliance
- **[Testing](/technical-docs/testing)** - E2E and unit testing
- **[Use Cases](/technical-docs/use-cases)** - Implementation use cases

## Platform Overview

The Videxa platform consists of three main applications:

| Application | Type | Description |
|-------------|------|-------------|
| AgentNexus | React Frontend | Healthcare claims management UI |
| AgentNexus Backend | FastAPI Python | Backend APIs and services |
| NexusChat | Full Stack | Chat application (React + Node.js) |

## Technology Stack

- **Frontend**: React 18, Tailwind CSS, React Router
- **Backend**: FastAPI (Python), Node.js (Express)
- **Database**: Snowflake (primary), Azure Table Storage (legacy)
- **Infrastructure**: Azure Container Apps, Docker
- **AI**: Snowflake Cortex, OpenAI integration

## Getting Help

For questions or issues, contact the development team via:
- Slack: #videxa-dev
- Email: dev@videxa.co
`,

  architecture: `# Architecture Overview

## Platform Architecture

The Videxa platform follows a microservices architecture with three main applications communicating via REST APIs and shared authentication.

\`\`\`mermaid
flowchart TB
    subgraph CDN["Azure Front Door"]
        AFD[CDN + SSL Termination]
    end

    subgraph Applications["Application Layer"]
        AN[AgentNexus Frontend<br/>React - Port 3000]
        AB[AgentNexus Backend<br/>FastAPI - Port 3080]
        NC[NexusChat<br/>React+Node.js - Port 3050]
    end

    subgraph Data["Data Layer"]
        SF[(Snowflake<br/>Database)]
        AZ[(Azure Blob<br/>Storage)]
    end

    AFD --> AN
    AFD --> AB
    AFD --> NC

    AN --> AB
    AN --> NC
    NC --> AB

    AB --> SF
    AB --> AZ
    NC --> SF
\`\`\`

## Key Design Decisions

### Snowflake-Only Architecture

The platform has migrated from MongoDB to Snowflake as the single source of truth:

- **Eliminated dual-storage** - No more MongoDB/Snowflake sync issues
- **HIPAA Compliance** - All data in Snowflake's compliant environment
- **Simplified queries** - Single query language (SQL)
- **Cost reduction** - Removed MongoDB cluster costs

### Single Sign-On (SSO)

Authentication is shared across all applications via JWT tokens:

1. User logs into AgentNexus
2. JWT token stored in sessionStorage
3. Token passed to NexusChat via URL parameter
4. NexusChat validates token with AgentNexus Backend
5. Session established across both apps

See [Authentication](/technical-docs/authentication) for implementation details.
`,

  authentication: `# Authentication Architecture

## Overview

The Videxa platform uses a unified authentication system based on JWT tokens, enabling SSO across AgentNexus and NexusChat.

## Authentication Flow

\`\`\`mermaid
sequenceDiagram
    participant User
    participant AN as AgentNexus
    participant BE as Backend
    participant NC as NexusChat

    User->>AN: Login Request
    AN->>BE: Verify Credentials
    BE-->>AN: JWT + Refresh Token
    AN-->>User: Store Tokens

    Note over User,NC: SSO Flow to NexusChat

    User->>AN: Click NexusChat
    AN->>NC: Redirect with Token
    NC->>BE: Validate Token
    BE-->>NC: User Profile
    NC-->>User: Session Ready
\`\`\`

## JWT Token Structure

\`\`\`javascript
{
  "user_id": "uuid",
  "email": "user@example.com",
  "email_verified": true,
  "token_type": "access",
  "exp": 1234567890,  // 15 min for access, 30 days for refresh
  "iat": 1234567800
}
\`\`\`

## Key Files

| File | Purpose |
|------|---------|
| \`agentnexus-backend/app/routers/auth_snowflake.py\` | Login endpoints |
| \`agentnexus/src/contexts/AuthContext.jsx\` | Frontend auth state |
| \`NexusChat/api/server/routes/sso.js\` | SSO validation |

## Security Considerations

- Access tokens expire in 15 minutes
- Refresh tokens expire in 30 days
- Tokens stored in sessionStorage (not localStorage)
- HTTPS required for all token transmission
`,

  'agentnexus-frontend': `# AgentNexus Frontend

## Overview

AgentNexus is a React 18 single-page application for healthcare claims management.

## Project Structure

\`\`\`
agentnexus/
├── src/
│   ├── components/      # Reusable UI components
│   │   ├── common/      # Shared components
│   │   ├── layout/      # Header, Footer, Layout
│   │   └── docs/        # Documentation components
│   ├── contexts/        # React contexts (Auth, Theme)
│   ├── pages/           # Page components
│   ├── services/        # API service functions
│   └── styles/          # CSS and theme files
├── public/              # Static assets
└── docs/                # Documentation
\`\`\`

## Key Technologies

- **React 18** - UI framework
- **React Router 6** - Client-side routing
- **Tailwind CSS** - Utility-first styling
- **Heroicons** - Icon library

## Development Commands

\`\`\`bash
# Start development server
npm start

# Build for production
npm run build

# Run tests
npm test
\`\`\`

## Authentication

The frontend uses \`AuthContext\` from \`src/contexts/AuthContext.jsx\`:

\`\`\`javascript
import { useAuth } from '../contexts/AuthContext';

const { user, isAuthenticated, login, logout } = useAuth();
\`\`\`

**Important**: Use \`contexts/AuthContext.jsx\`, not \`context/AuthContext.js\` (legacy).
`,

  'agentnexus-backend': `# AgentNexus Backend

## Overview

FastAPI-based backend providing REST APIs for the AgentNexus frontend.

## Project Structure

\`\`\`
agentnexus-backend/
├── app/
│   ├── routers/         # API endpoints
│   │   ├── auth_snowflake.py  # Authentication
│   │   ├── uploads.py         # File uploads
│   │   └── users.py           # User management
│   ├── services/        # Business logic
│   └── models/          # Data models
├── infrastructure/      # Azure deployment
└── docs/                # API documentation
\`\`\`

## API Endpoints

### Authentication
- \`POST /api/auth/login\` - User login
- \`POST /api/auth/register\` - User registration
- \`POST /api/auth/refresh\` - Token refresh
- \`POST /api/auth/logout\` - User logout

### File Uploads
- \`POST /api/uploads/request\` - Request upload URL
- \`POST /api/uploads/complete\` - Complete upload

## Development

\`\`\`bash
# Install dependencies
pip install -r requirements.txt

# Run development server
uvicorn app.main:app --reload --port 3080

# Run tests
pytest
\`\`\`
`,

  nexuschat: `# NexusChat

## Overview

Full-stack chat application built on LibreChat, customized for Videxa healthcare workflows.

## Architecture

- **Frontend**: React with Vite
- **Backend**: Node.js with Express
- **Database**: Snowflake (migrated from MongoDB)
- **AI**: Snowflake Cortex integration

## Key Features

- AI-powered healthcare chat
- File upload and analysis
- Cortex AI integration for insights
- SSO with AgentNexus

## Development

\`\`\`bash
# Start with Docker
docker-compose up -d

# View logs
docker-compose logs -f
\`\`\`

See the [NexusChat CLAUDE.md](file://c:/videxa-repos/NexusChat/CLAUDE.md) for detailed development instructions.
`,

  database: `# Database Architecture

## Snowflake Schema

All Videxa data is stored in Snowflake for HIPAA compliance and unified querying.

## Key Tables

| Table | Purpose |
|-------|---------|
| \`USER_PROFILES\` | User accounts and settings |
| \`USER_SESSIONS\` | Active sessions |
| \`PASSWORD_RESET_TOKENS\` | Password reset requests |
| \`AUDIT_LOG\` | Security audit trail |

## Connection Configuration

\`\`\`python
SNOWFLAKE_ACCOUNT = "your_account"
SNOWFLAKE_USER = "your_user"
SNOWFLAKE_DATABASE = "VIDEXA"
SNOWFLAKE_SCHEMA = "PUBLIC"
SNOWFLAKE_WAREHOUSE = "VIDEXA_WH"
\`\`\`

## External Stages

Azure Blob Storage integration via external stages:

- \`EDI_STAGE\` - EDI file uploads
- \`FHIR_STAGE\` - FHIR resources
`,

  deployment: `# Deployment Guide

## Environments

| Environment | Platform | URL |
|-------------|----------|-----|
| Development | Docker Desktop | localhost:3000, 3050, 3080 |
| Production | Azure Container Apps | *.videxa.ai |

## Docker Development

\`\`\`bash
# Start all services
docker-compose up -d

# View logs
docker-compose logs -f

# Rebuild after changes
docker-compose build --no-cache
docker-compose up -d
\`\`\`

## Azure Deployment

Production deployment uses Azure Container Apps:

1. Build Docker image
2. Push to Azure Container Registry
3. Update Container App revision
4. Verify health checks

See [Cloud Infrastructure skill](file://c:/videxa-repos/.claude/skills/cloud-infrastructure.md) for detailed procedures.
`,

  security: `# Security Documentation

## Security Audit Status

Last audit: January 30, 2026

| Category | Findings | Status |
|----------|----------|--------|
| Critical | 8 | Remediated |
| High | 12 | Remediated |
| Medium | 10 | In Progress |
| Low | 6 | Planned |

## HIPAA Compliance

- All PHI stored in Snowflake (HIPAA BAA in place)
- Encryption at rest and in transit
- Audit logging for all data access
- Access controls via role-based permissions

## Security Best Practices

1. Never commit secrets to git
2. Use Azure Key Vault for production secrets
3. Validate all user input
4. Use parameterized queries (prevent SQL injection)
5. Implement rate limiting on APIs
`,

  testing: `# Testing Documentation

## Test Infrastructure

| Type | Framework | Location |
|------|-----------|----------|
| Unit Tests | Jest | \`src/__tests__/\` |
| E2E Tests | Playwright | \`e2e/specs/\` |
| API Tests | Pytest | \`tests/\` |

## Running Tests

### AgentNexus Frontend
\`\`\`bash
npm test
\`\`\`

### AgentNexus Backend
\`\`\`bash
pytest
\`\`\`

### NexusChat E2E
\`\`\`bash
npx playwright test
\`\`\`

## Test Coverage Requirements

- Minimum 80% coverage for new features
- All use cases must have test cases
- Critical paths require E2E coverage
`,

  'use-cases': `# Use Cases

## Use Case Index

| UC | Title | Status | Coverage |
|----|-------|--------|----------|
| UC-001 | File Upload Completion Verification | Complete | 95% |
| UC-002 | Upload Audit Trail Snowflake | Complete | 90% |
| UC-003 | Snowflake External Stages Setup | Complete | 85% |
| UC-004 | Contract Rules Schema Setup | Complete | 80% |
| UC-005 | Cortex Analyst UI Completion | In Progress | 70% |
| UC-006 | Post Upload Processing Trigger | Complete | 88% |
| UC-007 | Snowpipe Manual Refresh | Complete | 82% |
| UC-008 | Data Quality Checks | Complete | 90% |
| UC-009 | AI Insights Generation | Complete | 85% |
| UC-010 | Snowflake Task Chain | Complete | 88% |
| UC-011 | Processing Complete Webhook | Complete | 92% |
| UC-012 | In-App Notification Display | Complete | 85% |
| UC-013 | Email Notification Delivery | Complete | 80% |
| UC-014 | User Notification Preferences | Complete | 82% |
| UC-015 | Code Generation UI | In Progress | 65% |

## Use Case Template

Each use case follows this structure:
- Overview
- Actors
- Preconditions
- Main Flow
- Alternative Flows
- Postconditions
- Test Cases (2-5 per use case)
- Coverage Matrix

See [Workflow Skill](file://c:/videxa-repos/.claude/skills/workflow.md) for use case requirements.
`
};

const TechnicalDocsPage = () => {
  const { '*': docPath } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeDoc, setActiveDoc] = useState('index');

  // Determine which doc to display based on URL path
  useEffect(() => {
    if (docPath) {
      // Remove trailing slash and get first path segment
      const path = docPath.replace(/\/$/, '').split('/')[0];
      if (docContent[path]) {
        setActiveDoc(path);
      } else {
        setActiveDoc('index');
      }
    } else {
      setActiveDoc('index');
    }
  }, [docPath]);

  // Get current doc content
  const content = docContent[activeDoc] || docContent.index;

  // Get category name for breadcrumb
  const currentCategory = docCategories.find(cat => cat.id === activeDoc);

  return (
    <div className="min-h-screen bg-white dark:bg-gray-900">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0E3056] to-[#1E4D7B] text-white py-8">
        <div className="container mx-auto px-4">
          <div className="flex items-center text-sm text-gray-300 mb-2">
            <button
              onClick={() => navigate('/dashboard')}
              className="hover:text-white transition-colors flex items-center"
            >
              <ArrowLeftIcon className="h-4 w-4 mr-1" />
              Dashboard
            </button>
            <ChevronRightIcon className="h-4 w-4 mx-2" />
            <span>Technical Documentation</span>
            {activeDoc !== 'index' && (
              <>
                <ChevronRightIcon className="h-4 w-4 mx-2" />
                <span className="text-white">{currentCategory?.name}</span>
              </>
            )}
          </div>
          <div className="flex items-center">
            <BookOpenIcon className="h-10 w-10 mr-4 text-primary-400" />
            <div>
              <h1 className="text-3xl font-bold">Technical Documentation</h1>
              <p className="text-gray-300 mt-1">
                Internal documentation for Videxa developers
              </p>
            </div>
          </div>
          <div className="mt-4 text-sm text-gray-400">
            Logged in as: <span className="text-primary-400">{user?.email}</span>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <div className="grid lg:grid-cols-4 gap-8 max-w-6xl mx-auto">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <DocsSidebar onCategorySelect={(id) => setActiveDoc(id)} />
          </div>

          {/* Content */}
          <div className="lg:col-span-3">
            <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-8">
              <MarkdownRenderer content={content} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TechnicalDocsPage;
